import { isEmptyValue } from './bookingGate';
import type { PaymentOption } from './payment-section/PaymentSection';
import type {
  BuyMembershipHostData,
  MembershipPaymentMethod,
  MembershipPlan,
  MembershipPriceService,
} from './buyMembership.types';
import { t } from '../../services/locale';

export const CARD = 'card';
export const DIRECT_DEBIT = 'direct_debit';
export const COMPLIMENTARY = 'complimentary';

// The user API returns `{}` rather than a nullish value when no card or mandate
// exists, so presence has to be checked by emptiness.
export function hasStoredPaymentDetails(
  data: BuyMembershipHostData,
  paymentKey: string
): boolean {
  if (paymentKey === CARD) {
    return !isEmptyValue(data.user?.card);
  }
  return paymentKey === DIRECT_DEBIT && !isEmptyValue(data.user?.mandate);
}

export function isCreditPack(plan: MembershipPlan): boolean {
  return plan.type === 'num_classes';
}

export function isSingleMembership(plan: MembershipPlan): boolean {
  return !plan.auto_renewal && !plan._subscription_plan_id;
}

/**
 * True when the dialog renders the purchase-terms checkbox already ticked and
 * locked, which means the member has visibly accepted before pressing Buy.
 */
export function purchaseTermsPreAccepted(data: BuyMembershipHostData): boolean {
  return (
    data.flags.isFtcEnabled &&
    !data.isCustomLink &&
    !isCreditPack(data.membership.planToBuy)
  );
}

export function isTrial(data: BuyMembershipHostData): boolean {
  return data.membershipsTrial.some(
    (membership) =>
      membership._id === data.membership._id && (data.membership.planToBuy.price || 0) === 0
  );
}

function hasActiveAcceptedMethod(plan: MembershipPlan, method: string): boolean {
  return Boolean(
    plan.accepted_payment_methods?.some(
      (payment) => payment.active && payment.type_id?.toLowerCase() === method
    )
  );
}

export function acceptsPayment(plan: MembershipPlan, method: string): boolean {
  if (method === CARD) {
    return (
      plan.subscription_plan_id === undefined ||
      plan.accepted_payment_methods === undefined ||
      hasActiveAcceptedMethod(plan, CARD)
    );
  }
  return method === DIRECT_DEBIT && hasActiveAcceptedMethod(plan, DIRECT_DEBIT);
}

function cardSuffix(data: BuyMembershipHostData): string {
  if (data.user?.parent_id && data.user.use_parent_card !== false) {
    return ` ${t('WEB_USE_PARENT_CARD')}`;
  }
  return data.user?.card?.last4 ? ` ***${data.user.card.last4}` : '';
}

function directDebitSuffix(data: BuyMembershipHostData): string {
  return data.user?.mandate?.account_number_ending
    ? ` ***${data.user.mandate.account_number_ending}`
    : '';
}

function paymentSuffix(
  data: BuyMembershipHostData,
  method: MembershipPaymentMethod
): string {
  return method.type_id?.toLowerCase() === CARD
    ? cardSuffix(data)
    : directDebitSuffix(data);
}

function isAvailablePaymentMethod(
  data: BuyMembershipHostData,
  method: MembershipPaymentMethod
): boolean {
  const key = method.type_id?.toLowerCase() || '';
  const available = method.active === true && method.staff_only === false;
  return (
    available &&
    method.type_id !== 'WALLET' &&
    acceptsPayment(data.membership.planToBuy, key)
  );
}

export function paymentOptions(data: BuyMembershipHostData): PaymentOption[] {
  return (data.branch?.paymentMethods || [])
    .filter((method) => isAvailablePaymentMethod(data, method))
    .map((method) => {
      const key = method.type_id?.toLowerCase() || '';
      return {
        id: key,
        key,
        value: `${t(method.type_id || '')}${paymentSuffix(data, method)}`,
      };
    });
}

function serviceName(data: BuyMembershipHostData): string {
  const plan = data.membership.planToBuy;
  const recurring = Boolean(plan.subscription_plan_id);
  const expiring = Boolean(
    plan.type !== 'credits' && plan.duration_time_unit_count && plan.duration_time_unit
  );
  const kind = t(recurring ? 'WEB_SUBSCRIPTION' : 'WEB_MEMBERSHIP');
  if (!expiring) {
    return kind;
  }
  const qualifier = t(recurring ? 'WEB_EVERY' : 'WEB_VALID_FOR').toLowerCase();
  return `${kind} ${qualifier} ${data.formatDuration(
    plan.duration_time_unit_count,
    plan.duration_time_unit
  )}`;
}

function joiningFeeService(
  data: BuyMembershipHostData,
  common: Pick<
    MembershipPriceService,
    'type' | 'service_id' | 'sub_service_id' | 'isCreditPack'
  >
): MembershipPriceService | null {
  const plan = data.membership.planToBuy;
  if (!plan.subscription_plan_id || !plan.upfront_fee) {
    return null;
  }
  return {
    ...common,
    name: t('WEB_UPFRONT_FEE'),
    price: plan.upfront_fee,
    is_promo_code_applicable: false,
  };
}

export function buildPriceServices(
  data: BuyMembershipHostData,
  selectedStartDate?: string
): MembershipPriceService[] {
  const plan = data.membership.planToBuy;
  const startDate = selectedStartDate
    ? { utc_start_date: data.toUtcDate(selectedStartDate) }
    : {};
  const common = {
    type: 'memberships' as const,
    service_id: data.membership._id || '',
    sub_service_id: plan.code === undefined ? undefined : String(plan.code),
    isCreditPack: isCreditPack(plan),
  };
  const services: MembershipPriceService[] = [
    {
      ...common,
      name: serviceName(data),
      price: plan.price || 0,
      is_promo_code_applicable: true,
      ...startDate,
    },
  ];
  const joiningFee = joiningFeeService(data, common);
  if (joiningFee) {
    services.push(joiningFee);
  }
  return services;
}

function membershipDurationLine(data: BuyMembershipHostData): string | null {
  const plan = data.membership.planToBuy;
  if (!isSingleMembership(plan)) {
    return null;
  }
  if (!plan.duration_time_unit_count) {
    return null;
  }
  if (!plan.duration_time_unit) {
    return null;
  }
  const key = plan.subscription_plan_id ? 'WEB_BILLED_EVERY' : 'WEB_VALID_FOR';
  return `${t(key)} ${data.formatDuration(
    plan.duration_time_unit_count,
    plan.duration_time_unit
  )}`;
}

function membershipCreditLines(data: BuyMembershipHostData) {
  return (data.membership.planToBuy.credits || []).map((credit) => {
    const expiry =
      credit.expiry?.interval_count && credit.expiry.interval
        ? ` ${t('WEB_VALID_FOR')} ${credit.expiry.interval_count} ${credit.expiry.interval}`
        : '';
    return {
      text: `${credit.num_sessions || 0} ${t(
        `${(credit.model || '').toUpperCase()}_CREDITS`
      )}${expiry}`,
    };
  });
}

function membershipPriceRows(data: BuyMembershipHostData) {
  const plan = data.membership.planToBuy;
  const rows = [];
  if (plan.price && isSingleMembership(plan)) {
    rows.push({
      label: t('WEB_PRICE'),
      amount: data.formatMoney(plan.price, data.currency),
    });
  }
  if (plan.subscription_amount) {
    rows.push({
      label: `${t('WEB_SUBSCRIPTION')} ${t(
        'WEB_EVERY'
      )} ${data.formatDuration(plan.duration_time_unit_count, plan.duration_time_unit)}`,
      amount: data.formatMoney(plan.subscription_amount, data.currency),
    });
  }
  if (plan.upfront_fee) {
    rows.push({
      label: t('WEB_JOINING_FEE'),
      amount: data.formatMoney(plan.upfront_fee, data.currency),
    });
  }
  return rows;
}

export function membershipSummary(data: BuyMembershipHostData) {
  return {
    planName: data.membership.planToBuy.name || '',
    durationLine: membershipDurationLine(data),
    creditLines: membershipCreditLines(data),
    priceRows: membershipPriceRows(data),
  };
}

export function newPaymentCollectorEnabled(data: BuyMembershipHostData): boolean {
  const plan = data.membership.planToBuy;
  if (isCreditPack(plan)) {
    return data.flags.paymentElementEnabled && data.flags.creditPackPaymentElementEnabled;
  }
  return (
    data.flags.paymentElementEnabled &&
    (isSingleMembership(plan) || data.flags.recurringPaymentElementEnabled)
  );
}

export function canQuoteMembershipPrice(data: BuyMembershipHostData): boolean {
  if ((data.membership.planToBuy.price || 0) <= 0) {
    return false;
  }
  if (!data.user?._id) {
    return false;
  }
  return data.user.membership?.status !== 'LOCKED';
}

export function shouldShowStartDate(
  data: BuyMembershipHostData,
  isProRated: boolean
): boolean {
  return Boolean(
    !isProRated &&
      data.flags.startDateEnabled &&
      !isCreditPack(data.membership.planToBuy) &&
      data.user?._id &&
      data.membership.planToBuy.starts_on === 'PURCHASE_DATE' &&
      data.user.membership?.type === 'payg'
  );
}
